import { INTRO_SLIDES } from '@deepwake/shared';

/**
 * Cinematic overlays: letterboxed full-screen interludes with a drifting
 * starfield and typewriter text. Used for the intro, story letters from
 * Sister Halex, and the shellcast death beat.
 */
export class Cinematics {
  private el: HTMLElement;
  private starCanvas: HTMLCanvasElement;
  private queue: (() => void)[] = [];
  private active = false;
  private typeTimer: number | null = null;

  constructor() {
    this.el = document.createElement('div');
    this.el.id = 'cinema';
    this.el.className = 'hidden';
    this.el.innerHTML = `
      <canvas id="cinema-stars"></canvas>
      <div class="cinema-bar top"></div>
      <div class="cinema-bar bottom"></div>
      <div class="cinema-content">
        <div class="cinema-title"></div>
        <div class="cinema-body"></div>
        <div class="cinema-objective"></div>
        <button class="cinema-next">Continue</button>
        <button class="cinema-skip">Skip</button>
      </div>`;
    document.body.appendChild(this.el);
    this.starCanvas = this.el.querySelector('#cinema-stars') as HTMLCanvasElement;
    this.el.querySelector('.cinema-next')!.addEventListener('click', () => this.advance());
    this.el.querySelector('.cinema-skip')!.addEventListener('click', () => this.close());
    this.drawStars();
    addEventListener('resize', () => this.drawStars());
  }

  private drawStars(): void {
    const c = this.starCanvas;
    c.width = innerWidth;
    c.height = innerHeight;
    const g = c.getContext('2d')!;
    g.fillStyle = '#03040a';
    g.fillRect(0, 0, c.width, c.height);
    for (let i = 0; i < 400; i++) {
      const b = 90 + Math.random() * 160;
      g.fillStyle = `rgba(${b},${b},${Math.min(255, b + 25)},${0.3 + Math.random() * 0.7})`;
      g.beginPath();
      g.arc(Math.random() * c.width, Math.random() * c.height, Math.random() * 1.4, 0, Math.PI * 2);
      g.fill();
    }
  }

  private show(title: string, body: string, objective: string, nextLabel: string): void {
    this.active = true;
    this.el.classList.remove('hidden');
    (this.el.querySelector('.cinema-title') as HTMLElement).textContent = title;
    (this.el.querySelector('.cinema-objective') as HTMLElement).textContent = objective;
    (this.el.querySelector('.cinema-next') as HTMLElement).textContent = nextLabel;
    const bodyEl = this.el.querySelector('.cinema-body') as HTMLElement;
    bodyEl.textContent = '';
    if (this.typeTimer !== null) clearInterval(this.typeTimer);
    let i = 0;
    this.typeTimer = window.setInterval(() => {
      i += 3;
      bodyEl.textContent = body.slice(0, i);
      if (i >= body.length && this.typeTimer !== null) {
        clearInterval(this.typeTimer);
        this.typeTimer = null;
      }
    }, 16);
    bodyEl.onclick = () => {
      if (this.typeTimer !== null) {
        clearInterval(this.typeTimer);
        this.typeTimer = null;
        bodyEl.textContent = body;
      }
    };
  }

  private advance(): void {
    const next = this.queue.shift();
    if (next) next();
    else this.close();
  }

  close(): void {
    this.queue = [];
    this.active = false;
    this.el.classList.add('hidden');
    if (this.typeTimer !== null) {
      clearInterval(this.typeTimer);
      this.typeTimer = null;
    }
  }

  /** First-login intro: the Stilling in four slides. */
  playIntro(onDone: () => void): void {
    const slides = [...INTRO_SLIDES];
    const showNext = (idx: number): void => {
      const s = slides[idx];
      const last = idx === slides.length - 1;
      this.show(s.title, s.body, '', last ? 'Wake' : 'Continue');
      this.queue = [
        () => {
          if (last) {
            this.close();
            onDone();
          } else showNext(idx + 1);
        },
      ];
    };
    showNext(0);
  }

  /** A letter from Sister Halex (story chapter). */
  playStory(chapter: number, title: string, body: string, objective: string, done: boolean): void {
    this.show(
      done ? `${title} — COMPLETE` : `Chapter ${chapter}: ${title}`,
      body,
      done ? '' : `Objective: ${objective}`,
      'Continue',
    );
    this.queue = [];
  }

  /** Shellcast death beat. */
  playShellcast(lost: string): void {
    this.show(
      'HULL LOST',
      `The ${lost} comes apart around you.\n\nFor one second there is no ship, no Reach, no light — only the core, holding your pattern the way a bell holds a note after the strike.\n\nThen the foundry lattice takes you, and somewhere in Hearthlight a printer begins to hum.`,
      'Shellcast complete. You keep the self. You lose the metal.',
      'Wake again',
    );
    this.queue = [];
  }

  isActive(): boolean {
    return this.active;
  }
}

/** Quick white-cyan flash used for gate jumps and warp exits. */
export function jumpFlash(): void {
  let el = document.getElementById('jumpflash');
  if (!el) {
    el = document.createElement('div');
    el.id = 'jumpflash';
    document.body.appendChild(el);
  }
  el.classList.remove('on');
  void el.offsetWidth; // restart animation
  el.classList.add('on');
}
